import { useEffect, useRef, useState } from 'react';

interface BackgroundAudioProps {
  src: string;
  volume?: number;
}

export default function BackgroundAudio({ src, volume = 0.3 }: BackgroundAudioProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Set audio properties
    audio.loop = true;
    audio.volume = volume;
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';

    // Audio event handlers
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleError = (e: Event) => {
      const error = (e.target as HTMLAudioElement).error;
      const errorMsg = error ? `Audio error: ${error.code} - ${error.message}` : 'Unknown audio error';
      console.error('🎵', errorMsg);
    };

    // Add event listeners
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);

    // Try to play audio
    const tryPlay = async () => {
      try {
        await audio.play();
      } catch (error) {
        // Set up user interaction listeners for autoplay fallback
        const handleUserInteraction = async () => {
          try {
            await audio.play();
          } catch (e) {
            console.error('🎵 Failed to play after user interaction:', e);
          }
        };

        // Listen for various user interactions
        const events = ['click', 'touchstart', 'keydown'];
        events.forEach(event => {
          document.addEventListener(event, handleUserInteraction, { once: true });
        });
      }
    };

    // Try to play when audio can play
    const handleCanPlay = () => tryPlay();
    audio.addEventListener('canplay', handleCanPlay);

    // Start trying to play immediately
    tryPlay();

    // Cleanup
    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [src, volume]);

  const toggleMute = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    console.log('🎵 Toggle mute clicked, current muted state:', isMuted);

    if (isMuted) {
      audio.muted = false;
      setIsMuted(false);
      console.log('🎵 Audio unmuted');
    } else {
      audio.muted = true;
      setIsMuted(true);
      console.log('🎵 Audio muted');
    }

    // If audio isn't playing, try to start it
    if (!isPlaying) {
      try {
        await audio.play();
        console.log('🎵 Audio started playing after mute toggle');
      } catch (error) {
        console.error('🎵 Failed to start audio after mute toggle:', error);
      }
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={src}
        loop
        preload="auto"
        autoPlay
        style={{ display: 'none' }}
      />
      
      {/* Glitchy Mute/Unmute Button */}
      <button
        onClick={toggleMute}
        className="fixed top-6 right-6 z-50 group"
        style={{
          background: 'linear-gradient(45deg, rgba(0,255,255,0.1), rgba(255,0,255,0.1))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0,255,255,0.3)',
        }}
      >
        <div className="relative p-3 overflow-hidden">
          {/* Glitch background layers */}
          <div
            className="absolute inset-0 opacity-20 animate-pulse"
            style={{
              background: `
                linear-gradient(90deg, transparent 0%, rgba(255,0,0,0.3) 25%, transparent 50%, rgba(0,255,0,0.3) 75%, transparent 100%),
                linear-gradient(0deg, transparent 0%, rgba(0,0,255,0.3) 50%, transparent 100%)
              `,
              animation: 'glitch-bg 2s infinite linear'
            }}
          />
          
          {/* Main icon */}
          <div className="relative z-10 w-6 h-6 flex items-center justify-center">
            {isMuted ? (
              // Muted icon with glitch effect
              <div className="relative">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-red-400 group-hover:text-red-300 transition-colors"
                  style={{
                    filter: 'drop-shadow(0 0 8px rgba(255,0,0,0.5))',
                    animation: 'glitch-icon 3s infinite'
                  }}
                >
                  <path d="M16 9a5 5 0 0 1 0 6m2.5-8.5a9 9 0 0 1 0 11M6.3 6.3a1 1 0 0 0-.7.7L4 8H2a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2l1.6 1.1a1 1 0 0 0 1.4-.1V9a1 1 0 0 0-.7-.7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="22" y1="2" x2="2" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            ) : (
              // Unmuted icon with glitch effect
              <div className="relative">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-cyan-400 group-hover:text-cyan-300 transition-colors"
                  style={{
                    filter: 'drop-shadow(0 0 8px rgba(0,255,255,0.5))',
                    animation: 'glitch-icon 3s infinite'
                  }}
                >
                  <path d="M6 8H2a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h4l5.5 3.5A1 1 0 0 0 13 19V5a1 1 0 0 0-1.5-.9L6 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M16 9a5 5 0 0 1 0 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M19 6a9 9 0 0 1 0 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            )}
          </div>

          {/* Glitch overlay effects */}
          <div
            className="absolute inset-0 pointer-events-none opacity-30"
            style={{
              background: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
              animation: 'glitch-lines 1.5s infinite linear'
            }}
          />
        </div>

        {/* Hover glow effect */}
        <div
          className="absolute inset-0 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,255,0.2) 0%, transparent 70%)',
            filter: 'blur(8px)'
          }}
        />
      </button>

      {/* CSS animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes glitch-bg {
            0%, 100% { transform: translateX(0); }
            10% { transform: translateX(-2px) skew(-1deg); }
            20% { transform: translateX(2px) skew(1deg); }
            30% { transform: translateX(-1px) skew(-0.5deg); }
            40% { transform: translateX(1px) skew(0.5deg); }
            50% { transform: translateX(0); }
          }

          @keyframes glitch-icon {
            0%, 90%, 100% { transform: translateX(0); }
            91% { transform: translateX(-1px); }
            92% { transform: translateX(1px); }
            93% { transform: translateX(-1px); }
            94% { transform: translateX(1px); }
            95% { transform: translateX(0); }
          }

          @keyframes glitch-lines {
            0% { transform: translateX(0); }
            25% { transform: translateX(-2px); }
            50% { transform: translateX(2px); }
            75% { transform: translateX(-1px); }
            100% { transform: translateX(0); }
          }
        `
      }} />
    </>
  );
}